import { getCurrentCulture } from "client_legacy/ndaw/culture";
import { isNilOrWhiteSpace, isNotNilOrWhiteSpace } from "./utils";

export type VideoOrListId = {
	videoId: string;
	listId?: string | nil;
} | {
	listId: string;
	videoId?: string | nil;
} | {
	videoId: string;
	listId: string;
};

export type YouTubeEmbedOptions = VideoOrListId & {
	forceSubtitles?: boolean;
	culture?: string;
	autoplay?: boolean;
};

export function generateEmbedUrl(options: YouTubeEmbedOptions): string {
	let {
		videoId,
		listId,
		forceSubtitles,
		culture,
		autoplay
	} = options;

	culture = culture || getCurrentCulture();

	let result: string = "https://www.youtube.com/embed/";
	if (videoId) {
		const videoIdUriComponent: string = encodeURIComponent(videoId);
		result += `${videoIdUriComponent}/`;
	}

	result += `?hl=${culture}`; // set player language

	if (listId) {
		const listIdUriComponent: string = encodeURIComponent(listId);
		result += `&list=${listIdUriComponent}`;
	}

	if (forceSubtitles) {
		result += `&cc_load_policy=1`;
	}

	if (autoplay) {
		result += `&autoplay=1`;
	}

	result += `&cc_lang_pref=${culture}`; // set subtitles language

	return result;
}

export function isYoutubeVideoUrl(url: string): boolean {
	const parseResult: TryResult<VideoOrListId> = tryParseYouTubeUrl(url);

	if (!parseResult.wasSuccessful) {
		return false;
	}

	if (isNilOrWhiteSpace(parseResult.result.videoId)) {
		return false;
	}

	return true;
}

export function getIdFromYoutubeUrl(url: string): TryResult<string> {
	const parseResult: TryResult<VideoOrListId> = tryParseYouTubeUrl(url);

	if (!parseResult.wasSuccessful) {
		return { wasSuccessful: false };
	}

	const { result } = parseResult;

	if (isNotNilOrWhiteSpace(result.videoId)) {
		return {
			wasSuccessful: true,
			result: result.videoId
		};
	}

	return { wasSuccessful: false };
}

export function getYoutubeVideoThumbnailUrlFromId(videoId: string): string {
	return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
}

export function getYoutubeVideoThumbnailFromUrl(videoUrl: string): string {
	const videoInfo: TryResult<string> = getIdFromYoutubeUrl(videoUrl);
	if (!videoInfo.wasSuccessful) {
		throw new Error(`Could not retrieve video id from: ${videoUrl}`);
	}

	return getYoutubeVideoThumbnailUrlFromId(videoInfo.result);
}

export function tryParseYouTubeUrl(value: string): TryResult<VideoOrListId> {
	if (!URL.canParse(value)) {
		return {
			wasSuccessful: false
		};
	}

	const url = new URL(value);
	switch (url.protocol) {
		case "https:":
			if (url.port !== "" && url.port !== "443") {
				return {
					wasSuccessful: false
				};
			}
			break;

		default:
			return {
				wasSuccessful: false
			};
	}

	for (const parser of parsers) {
		const result: TryResult<VideoOrListId> = parser.tryParse(url);

		if (result.wasSuccessful) {
			return result;
		}
	}

	return {
		wasSuccessful: false
	};
}

interface ISpecificYouTubeUrlParser {
	tryParse(url: URL): TryResult<VideoOrListId>;
}

// Keep in sync with YouTubeUrlParser.cs NormalYouTubeUrlParser
class NormalYouTubeUrlParser implements ISpecificYouTubeUrlParser {
	private static readonly _expectedHosts: readonly string[] = ["www.youtube.com", "youtube.com"];
	private static readonly _expectedPaths: readonly string[] = ["/watch", "/playlist"];
	private static readonly VIDEO_PARAM_NAME: string = "v";
	private static readonly LIST_PARAM_NAME: string = "list";

	public tryParse(url: URL): TryResult<VideoOrListId> {
		if (!NormalYouTubeUrlParser._expectedHosts.includes(url.hostname)) {
			return { wasSuccessful: false };
		}

		if (!NormalYouTubeUrlParser._expectedPaths.includes(url.pathname)) {
			return { wasSuccessful: false };
		}

		const queryParams: URLSearchParams = url.searchParams;
		const videoId: string | null = queryParams.get(NormalYouTubeUrlParser.VIDEO_PARAM_NAME);
		const playlistId: string | null = queryParams.get(NormalYouTubeUrlParser.LIST_PARAM_NAME);

		if (isNotNilOrWhiteSpace(videoId) || isNotNilOrWhiteSpace(playlistId)) {
			return {
				wasSuccessful: true,
				// @ts-expect-error: TS cannot track that atleast 1 of them (videoId or playlistId) is set
				result: {
					videoId,
					listId: playlistId
				}
			};
		}

		return { wasSuccessful: false };
	}
}

// Keep in sync with YouTubeUrlParser.cs ShortYouTubeUrlParser
class ShortYouTubeUrlParser implements ISpecificYouTubeUrlParser {
	private static readonly _expectedHosts: readonly string[] = ["youtu.be"];
	private static readonly LIST_PARAM_NAME: string = "list";

	public tryParse(url: URL): TryResult<VideoOrListId> {
		if (!ShortYouTubeUrlParser._expectedHosts.includes(url.hostname)) {
			return { wasSuccessful: false };
		}

		// NOTE: segments is different from C# Uri.Segments in that it doesn't include the "/" in them
		const segments: readonly string[] = url.pathname.split("/");

		if (segments.length < 2) {
			return { wasSuccessful: false };
		}

		const videoId: string = segments[1];
		const queryParams: URLSearchParams = url.searchParams;
		const playlistId: string | null = queryParams.get(ShortYouTubeUrlParser.LIST_PARAM_NAME);

		return {
			wasSuccessful: true,
			result: {
				videoId,
				listId: playlistId
			}
		};
	}
}

// Keep in sync with YouTubeUrlParser.cs ShortsYouTubeUrlParser
class ShortsYouTubeUrlParser implements ISpecificYouTubeUrlParser {
	private static readonly _expectedHosts: readonly string[] = ["www.youtube.com", "youtube.com"];

	public tryParse(url: URL): TryResult<VideoOrListId> {
		if (!ShortsYouTubeUrlParser._expectedHosts.includes(url.hostname)) {
			return { wasSuccessful: false };
		}

		// NOTE: segments is different from C# Uri.Segments in that it doesn't include the "/" in them
		const pathname: string = url.pathname.endsWith("/")
			? url.pathname.slice(0, -1)
			: url.pathname;

		const segments: readonly string[] = pathname.split("/");

		if (segments.length !== 3) {
			return { wasSuccessful: false };
		}

		const path: string = segments[1];
		if (path !== "shorts") {
			return { wasSuccessful: false };
		}

		const videoId: string = segments[2];

		if(isNilOrWhiteSpace(videoId))
		{
			return { wasSuccessful: false };
		}

		return {
			wasSuccessful: true,
			result: {
				videoId,
				listId: null
			}
		};
	}
}

const parsers: readonly ISpecificYouTubeUrlParser[] = [
	new NormalYouTubeUrlParser(),
	new ShortYouTubeUrlParser(),
	new ShortsYouTubeUrlParser()
];
